/**
 * Production-safe logger utility
 * 
 * In production builds, only error and warn levels are logged.
 * In development, all levels are logged with enhanced formatting.
 * 
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.info('User signed in', { userId: '123' });
 *   logger.error('Failed to fetch data', error);
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: unknown;
  timestamp: string;
  context?: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// In production, only show warnings and errors
const MIN_LEVEL: LogLevel = import.meta.env.PROD ? 'warn' : 'debug';

const shouldLog = (level: LogLevel): boolean => {
  return LOG_LEVELS[level] >= LOG_LEVELS[MIN_LEVEL];
};

const formatMessage = (context: string | undefined, message: string): string => {
  return context ? `[${context}] ${message}` : message;
};

const createLogger = (context?: string) => {
  const log = (level: LogLevel, message: string, data?: unknown): void => {
    if (!shouldLog(level)) return;

    const formattedMessage = formatMessage(context, message);
    const timestamp = new Date().toISOString();

    const entry: LogEntry = {
      level,
      message: formattedMessage,
      data,
      timestamp,
      context,
    };

    switch (level) {
      case 'debug':
        console.debug(`🔍 ${formattedMessage}`, data ?? '');
        break;
      case 'info':
        console.info(`ℹ️ ${formattedMessage}`, data ?? '');
        break;
      case 'warn':
        console.warn(`⚠️ ${formattedMessage}`, data ?? '');
        break;
      case 'error':
        console.error(`❌ ${formattedMessage}`, data ?? '');
        // Also capture in error tracking for production
        if (import.meta.env.PROD && data instanceof Error) {
          // Could integrate with Sentry or error_logs table here
        }
        break;
    }

    // In development, also log the full entry for debugging
    if (import.meta.env.DEV && level === 'debug') {
      console.groupCollapsed(`📋 Full log entry`);
      console.table(entry);
      console.groupEnd();
    }
  };

  return {
    debug: (message: string, data?: unknown) => log('debug', message, data),
    info: (message: string, data?: unknown) => log('info', message, data),
    warn: (message: string, data?: unknown) => log('warn', message, data),
    error: (message: string, data?: unknown) => log('error', message, data),
    
    /**
     * Create a child logger with a specific context prefix
     */
    child: (childContext: string) => createLogger(
      context ? `${context}:${childContext}` : childContext
    ),
  };
};

/**
 * Default logger instance
 */
export const logger = createLogger();

/**
 * Create a logger with a specific context
 * @example
 * const authLogger = createContextLogger('Auth');
 * authLogger.info('User logged in');
 * // Output: [Auth] User logged in
 */
export const createContextLogger = (context: string) => createLogger(context);

/**
 * Realtime-specific logger (for subscription debugging)
 */
export const realtimeLogger = createLogger('Realtime');

/**
 * Data source logger (for tracking demo vs production data)
 */
export const dataSourceLogger = createLogger('DataSource');

/**
 * Performance logger (for timing operations)
 */
export const perfLogger = createLogger('Performance');

/**
 * Security logger (for auth and access control)
 */
export const securityLogger = createLogger('Security');

/**
 * Test mode logger
 */
export const testModeLogger = createLogger('TestMode');

/**
 * Measure and log operation duration
 */
export const measureDuration = async <T>(
  operation: () => Promise<T>,
  operationName: string,
  warnThresholdMs = 1000
): Promise<T> => {
  const start = performance.now();
  try {
    return await operation();
  } finally {
    const duration = performance.now() - start;
    if (duration > warnThresholdMs) {
      perfLogger.warn(`Slow operation: ${operationName}`, { durationMs: Math.round(duration) });
    } else {
      perfLogger.debug(`${operationName} completed`, { durationMs: Math.round(duration) });
    }
  }
};
